import React from 'react';
import styled from 'styled-components';
import { Typography } from '../Typography/Typography';
import Button, { ButtonProps, RouterLinkProps } from '../Button/Button';
import CheckCircleIcon from '../../assets/icons/checkCircleFilled.svg?react';
import LightBulbFilledIcon from '../../assets/icons/lightBulb.svg?react';
import WarningIcon from '../../assets/icons/warning.svg?react';
import { breakpoints } from '../../tokens/breakpoints';
import { Badge } from '../Badge/Badge';

export type StatusType = 'success' | 'in_progress' | 'error';

export type StatusBarConfig = {
  status: StatusType;
  message: string;
};

export type DynamicStepCardProps = {
  /**
   * Icon component to display alongside title and subtitle
   */
  icon: React.ReactNode;
  /**
   * Main title text (17px)
   */
  title: string;
  /**
   * Optional badge text
   */
  badgeText?: string;
  /**
   * Subtitle text (14px, dark gray)
   */
  subtitle: string;
  /**
   * Optional status bar configuration
   */
  statusBar?: StatusBarConfig;
  /**
   * Whether the card should appear disabled (opacity 0.3)
   */
  disabled?: boolean;
  /**
   * Whether the step is completed. When true, shows checkmark icon and applies completed styling.
   */
  isCompleted?: boolean;
  /**
   * Optional primary action button (small size)
   */
  primaryButton?: (ButtonProps | RouterLinkProps) & { label: string };
  /**
   * Optional secondary action button (small size)
   */
  secondaryButton?: (ButtonProps | RouterLinkProps) & { label: string };
};

const CardContainer = styled.div<{ $isCompleted?: boolean }>`
  display: flex;
  padding: 16px 24px;
  flex-direction: column;
  align-items: flex-start;
  gap: 6px;
  align-self: stretch;
  border-radius: 8px;
  border: 1px solid ${({ $isCompleted }) => ($isCompleted ? 'var(--green-100)' : 'var(--grayscale-50)')};
  background: ${({ $isCompleted }) => ($isCompleted ? 'var(--white)' : 'var(--surface-100)')};
`;

const TopRow = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
  align-self: stretch;
  min-height: 64px;

  @media (max-width: ${breakpoints.tabletMax}px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }
`;

const IconContainer = styled.div<{ $disabled?: boolean }>`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 46px;

  ${({ $disabled }) =>
    $disabled &&
    `
    opacity: 0.3;
  `}
`;

const TextContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  flex: 1;

  @media (max-width: ${breakpoints.tabletMax}px) {
    align-self: stretch;
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  flex-shrink: 0;

  @media (max-width: ${breakpoints.tabletMax}px) {
    align-self: stretch;
    justify-content: flex-end;
  }

  @media (max-width: ${breakpoints.mobileMax}px) {
    flex-direction: column;
  }
`;

const StyledButton = styled(Button)`
  width: 148px;

  @media (max-width: ${breakpoints.mobileMax}px) {
    width: 100%;
  }
`;

const StatusRow = styled.div<{ $status: StatusType }>`
  display: flex;
  align-items: center;
  gap: 8px;
  align-self: stretch;
  padding: 12px;
  border-radius: var(--border-radius-medium);
  border: 1px solid var(--grayscale-50);

  ${({ $status }) => {
    switch ($status) {
      case 'success':
        return `
          color: var(--green-400);
          background: var(--green-50);
        `;
      case 'in_progress':
        return `
          color: var(--purple-700);
          background: var(--purple-200);
        `;
      case 'error':
        return `
          color: var(--red-800);
          // TODO(tokens): Replace legacy color token --extra-light-red with numbered equivalent once available.
          background: var(--extra-light-red);
        `;
      default:
        return '';
    }
  }}
`;

const StatusIconContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  flex-shrink: 0;

  svg {
    width: 16px;
    height: 16px;
  }
`;

const getStatusIcon = (status: StatusType): React.ReactNode => {
  switch (status) {
    case 'success':
      return <CheckCircleIcon aria-hidden />;
    case 'in_progress':
      return <LightBulbFilledIcon aria-hidden />;
    case 'error':
      return <WarningIcon aria-hidden />;
    default:
      return null;
  }
};

const TitleWithBadgeContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
`;

/**
 * DynamicStepCard component that displays a card with icon, title, subtitle, optional action buttons, and optional status bar.
 */
const DynamicStepCard = ({
  icon,
  title,
  badgeText,
  subtitle,
  statusBar,
  disabled,
  isCompleted,
  primaryButton,
  secondaryButton,
}: DynamicStepCardProps) => {
  const textColor = disabled ? 'var(--grayscale-300)' : 'var(--grayscale-900)';

  return (
    <CardContainer $isCompleted={isCompleted}>
      <TopRow>
        <IconContainer $disabled={disabled}>{icon}</IconContainer>
        <TextContainer>
          <TitleWithBadgeContainer>
            <Typography color={textColor} styledAs="bodyLargeDMSans" weight={600}>
              {title}
            </Typography>
            {isCompleted && <CheckCircleIcon className="size-4 text-green-300" title="completed" />}
            {badgeText && <Badge variant="primary">{badgeText}</Badge>}
          </TitleWithBadgeContainer>
          <Typography color={textColor} styledAs="bodySmallDMSans">
            {subtitle}
          </Typography>
        </TextContainer>
        {(primaryButton || secondaryButton) && !disabled && (
          <ButtonContainer>
            {secondaryButton && (
              <StyledButton
                {...secondaryButton}
                size="sm"
                variant="secondary"
                disabled={disabled || secondaryButton.disabled}
              >
                {secondaryButton.label}
              </StyledButton>
            )}
            {primaryButton && (
              <StyledButton
                {...primaryButton}
                size="sm"
                variant="primary"
                disabled={disabled || primaryButton.disabled}
              >
                {primaryButton.label}
              </StyledButton>
            )}
          </ButtonContainer>
        )}
      </TopRow>

      {statusBar && !disabled && (
        <StatusRow $status={statusBar.status}>
          <StatusIconContainer>{getStatusIcon(statusBar.status)}</StatusIconContainer>
          <Typography styledAs="bodySmallDMSans" color="currentColor" weight={600}>
            {statusBar.message}
          </Typography>
        </StatusRow>
      )}
    </CardContainer>
  );
};

export default DynamicStepCard;
